// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import clsx from 'clsx';
import * as KeyCodes from 'keycode-js';

import { Icon } from 'office-ui-fabric-react/lib/Icon';

import Styles from './toggle.module.scss';
import { useTranslation } from 'react-i18next';

interface PropsType {
    chevronColor: string;
    children: JSX.Element;
    colorOnClose: string;
    colorOnOpen: string;
    handleToggle: (isOpen: boolean) => void;
    isOpen: boolean;
    tableName?: string;
}

interface ToggleChevronProps {
    isOpen: boolean;
    key?: string;
    onChevronKeyDown: any;
    tableName?: string;
    chevronColor: string;
    chevronAriaLabel?: string;
}

function RenderChevron(props: ToggleChevronProps) {
    const { t } = useTranslation();

    const className = clsx(
        Styles.chevron,
        Styles[props.chevronColor]
    );
    const translationLabel = props.isOpen ? 'button_expanded_message' : 'button_collapsed_message';
    const translationProps = { tableName: props.chevronAriaLabel || props.tableName };
    return (
        <Icon
            aria-label={t(translationLabel, translationProps)}
            className={className}
            iconName={props.isOpen ? 'ChevronDown' : 'ChevronRight'}
            tabIndex={0}
            role="button"
            onKeyDown={props.onChevronKeyDown}
            key={props.key}
        />
    );
}

export default function Toggle(props: PropsType): JSX.Element {

    function onClick() {
        props.handleToggle(!props.isOpen);
    }

    const onChevronKeyDown = (e: any) => {
        if (e.keyCode === KeyCodes.KEY_RETURN || e.keyCode === KeyCodes.KEY_SPACE) {
            e.preventDefault();
            e.stopPropagation();
            onClick();
        }
    };

    const className = clsx(
        Styles.toggle,
        Styles.backgroundColor,
        {
            [Styles[props.colorOnOpen]]: props.isOpen,
        },
        {
            [Styles[props.colorOnClose]]: !props.isOpen,
        }
    );

    return (
        <div id="toggle" className={className} onClick={onClick}>
            {/*This is the fix to prevent focus loss. When used as a tag, it gets dismounted, when used as a function, it doesn't*/}
            {RenderChevron({ ...props, onChevronKeyDown, key: props.tableName })}
            {props.children}
        </div>
    );
}
